import { AfterViewInit, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { OidcSignupFacade } from '@ridango/components';
import Env, { RegistrationFieldOrderConfig } from '@ridango/core/env';
import { phoneValidNumberValidator } from '@ridango/core/lib/validators/phone.validator';

@Component({
    selector: 'rwidget-signup-complete-form',
    templateUrl: './signup-complete-form.component.html',
    styleUrls: ['./signup-complete-form.component.scss']
})
export class SignupCompleteFormComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('PHONE_NUMBER') PHONE_NUMBER: TemplateRef<unknown>;
    @ViewChild('PERSONAL_CODE') PERSONAL_CODE: TemplateRef<unknown>;

    signupCompleteForm: FormGroup;
    fieldOrderKeys: string[];
    templateMap: { [key: string]: TemplateRef<unknown> } = {};
    loading$ = this.oidcSignupFacade.loading$;

    constructor(
        @Inject('ENV') public env: Env,
        private readonly oidcSignupFacade: OidcSignupFacade
    ) {}

    ngOnInit(): void {
        const fieldOrderConfig = this.getFieldOrderConfig();
        this.fieldOrderKeys = Object.keys(fieldOrderConfig);
        this.signupCompleteForm = new FormGroup(this.buildFormControls(fieldOrderConfig));
    }

    private buildFormControls(fieldOrderConfig: RegistrationFieldOrderConfig): { [key: string]: AbstractControl } {
        const formOptions: { [key: string]: AbstractControl } = {};

        if (this.fieldOrderKeys.includes('PHONE_NUMBER')) {
            formOptions.phoneNumber = this.createPhoneNumberControl(fieldOrderConfig);
        }

        if (this.fieldOrderKeys.includes('PERSONAL_CODE')) {
            formOptions.personalCode = this.createPersonalCodeControl(fieldOrderConfig);
        }

        return formOptions;
    }

    private createPhoneNumberControl(fieldOrderConfig: RegistrationFieldOrderConfig): FormControl {
        const initialValue = this.env?.REGISTRATION?.USE_COUNTRY_CODE_ON_FORMS
            ? this.env?.DEFAULT_PHONE_COUNTRY_CODE ?? ''
            : '';
        const validators = [
            fieldOrderConfig.PHONE_NUMBER?.OPTIONAL ? () => null : Validators.required,
            phoneValidNumberValidator
        ];
        return new FormControl(initialValue, validators);
    }

    private createPersonalCodeControl(fieldOrderConfig: RegistrationFieldOrderConfig): FormControl {
        const validators = [fieldOrderConfig.PERSONAL_CODE?.OPTIONAL ? () => null : Validators.required];
        return new FormControl('', validators);
    }

    ngAfterViewInit(): void {
        const fieldOrderConfig = this.getFieldOrderConfig();
        Object.keys(fieldOrderConfig).forEach((fieldKey) => {
            this.templateMap[fieldKey] = this[fieldKey];
        });
    }

    ngOnDestroy(): void {
        this.oidcSignupFacade.reset();
    }

    completeSignup(): void {
        if (!this.signupCompleteForm.valid) {
            this.signupCompleteForm.markAllAsTouched();
            return;
        }

        const payload = this.signupCompleteForm.getRawValue();
        this.oidcSignupFacade.completeSignup({
            phoneNumber: payload.phoneNumber,
            personalCode: payload.personalCode
        });
    }

    isSubmitButtonDisabled(): boolean {
        return !this.signupCompleteForm?.valid;
    }

    private getFieldOrderConfig(): RegistrationFieldOrderConfig {
        return (
            this.env.REGISTRATION?.OIDC_SIGNUP_COMPLETE_FIELD_ORDER ?? {
                PHONE_NUMBER: { OPTIONAL: false },
                PERSONAL_CODE: { OPTIONAL: false }
            }
        );
    }
}
